import type { EntityData, CampaignUnit } from '@/services/api/types';
import { pushEvent, useDataLayer, useLoader } from '@CS/hooks';
import { getUserEntityData } from '@/redux/user/selectors';
import HOTSPOTS from '@/components/Map/hotspots';
import { openHome } from '@/services/app/mcd';
import { useState, useEffect } from 'react';
import HomeWrapper from './Home.styles';
import { connect } from 'react-redux';
import Menu from '@/components/Menu';
import Map from '@/components/Map';
import API from '@/services/api';

import {
  Hotspot,
  Welcome,
  Onboarding,
  TRANSITION_DURATION
} from '@/components/Popups';

type HomeProps = {
  data?: EntityData;
};

const Home = ({ data }: HomeProps) => {
  const [step, setStep] = useState<number>();
  const [loading, setLoading] = useState(true);

  const [deal, setDeal] = useState<CampaignUnit>();
  const [hotspot, setHotspot] = useState<number>();

  const [mapLoaded, setMapLoaded] = useState(false);
  const loader = useLoader(loading, { image: true });

  const closeHotspotPopup = () => setHotspot(undefined);
  const onNext = () => setStep((step) => (step ?? 0) + 1);
  const [welcomePopup, setWelcomePopup] = useState(false);

  const [onboardingPopup, setOnboardingPopup] = useState(false);
  const openOnboardingPopup = () => ~setStep(0) && setOnboardingPopup(true);

  const closeOnboardingPopup = () => {
    setOnboardingPopup(false);
    deal && setHotspot(0);
  };

  const onClick = (index: number) =>
    ~setHotspot(index) &&
    pushEvent({
      eventLabel: `GGA25/island/${HOTSPOTS[index].type}`,
      eventAction: 'island',
      event: 'button_click'
    });

  const closeWelcomePopup = () => {
    setOnboardingPopup(true);
    setWelcomePopup(false);
    setStep(0);
  };

  useEffect(() => {
    setLoading(!data);

    const onLoad = async () => {
      const { welcome, onboardedAt, skipOnboarding } = data as EntityData;
      const onboarded = new Date(`${onboardedAt}`).toDateString();
      const today = new Date().toDateString();

      const onboarding = !skipOnboarding && today !== onboarded;
      const deal = await API.getCampaignUnits();
      setDeal(deal);

      !(!welcome || onboarding)
        ? deal && setHotspot(0)
        : setTimeout(() => {
            if (!welcome) setWelcomePopup(true);
            else if (onboarding) setOnboardingPopup(true);
          }, TRANSITION_DURATION / 3);
    };

    !!data && onLoad();
  }, [data]);

  useDataLayer({
    event: 'page_view',
    eventProperties: {
      page_virtual_path: '/GGA25/island',
      page_type: 'Golden_GetAway_2025'
    }
  });

  return (
    loader || (
      <HomeWrapper>
        <Map step={step} onClick={onClick} setMapLoaded={setMapLoaded} />

        {mapLoaded && (
          <Menu onClose={openHome} onOnboarding={openOnboardingPopup} />
        )}

        {typeof hotspot === 'number' && (
          <Hotspot onClose={closeHotspotPopup} hotspot={hotspot} deal={deal} />
        )}

        {mapLoaded && welcomePopup && (
          <Welcome data={data} onClose={closeWelcomePopup} />
        )}

        {mapLoaded && onboardingPopup && (
          <Onboarding
            onClose={closeOnboardingPopup}
            step={step ?? 0}
            onNext={onNext}
            data={data}
          />
        )}
      </HomeWrapper>
    )
  );
};

const mapStateToProps = (state: never) => ({
  data: getUserEntityData(state)
});

export default connect(mapStateToProps)(Home);
